import { IAccountImpact, IUnSdg } from 'src/types'
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'

import { useCustomModal } from 'src/hooks'
import { AccountImpactDetails } from './account-impact-details'
import { CustomModalAnimated } from '../modal-animated'
import { WorldHeartIcon } from 'src/assets/icons/v2/WorldHeartIcon'
import { AIGeneratedBanner } from '../ai-generated-banner'
import { BlockchainVerifiedBanner } from '../blockchain-verified-banner'

interface IProps {
  title: string | number
  subtitle: string
  image: string
  unSdg: Array<IUnSdg>
  quantityName: string
  item: IAccountImpact
  walletAddress?: string
  showWalletAddress: boolean
  showBcInfo: boolean
  accountImage?: string
  bannerStroke: string
}

export const AccountImpactItem = ({
  image,
  title,
  subtitle,
  quantityName,
  item,
  walletAddress,
  showWalletAddress,
  showBcInfo,
  accountImage,
  bannerStroke,
}: IProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { handleClose, handleOpen, open } = useCustomModal()

  const isEmpty = !image || image.trim().length === 0
  const isUiAvatar = image?.includes('ui-avatars.com/api')
  const showFallback = isEmpty || isUiAvatar

  return (
    <Box
      id="account-impacts-item__components-account-impacts__box__l30"
      sx={{ position: 'relative', cursor: 'pointer', pt: item?.ai_generated ? '22px' : 0 }}
    >
      <Box
        id="account-impacts-item__components-account-impacts__box__l31"
        onClick={handleOpen}
        sx={{
          height: 'auto',

          transition: 'transform 200ms, filter 200ms',
          willChange: 'transform, filter',
          '&:hover': {
            filter: ' brightness(90%)',
            transform: 'scale(0.98)',
          },
        }}
      >
        <Box
          id="account-impacts-item__components-account-impacts__box__l44"
          sx={{
            width: '120px',
            maxHeight: '200px',
            minHeight: '165px',

            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',

            borderRadius: '7px',
            background: theme.palette.background.default,
            boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.10)',
            overflow: 'hidden',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box
            id="account-impacts-item__components-account-impacts__box__l62"
            sx={
              showFallback
                ? {
                    width: '100%',
                    height: '110px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: 'none',
                  }
                : {
                    backgroundImage: `url(${image})`,
                    width: '100%',
                    backgroundPosition: 'center',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    height: '110px',
                  }
            }
          >
            {showFallback && <WorldHeartIcon size={80} color={'#666'} />}
          </Box>
          <Box
            id="account-impacts-item__components-account-impacts__box__l85"
            sx={{
              display: 'flex',
              height: '100%',
              flexGrow: 1,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: title ? 'flex-start' : 'center',
              py: '4px',
              px: '8px',
            }}
          >
            {title && (
              <Box
                id="account-impacts-item__components-account-impacts__box__l98"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  columnGap: '2px',
                  alignItems: 'baseline',
                  pb: '4px',
                }}
              >
                <Typography
                  id="account-impacts-item__components-account-impacts__typography__l108"
                  sx={{ textOverflow: 'ellipsis', textAlign: 'center', wordBreak: 'break-word' }}
                  variant="h4"
                >
                  {title}
                </Typography>
                <Typography
                  id="account-impacts-item__components-account-impacts__typography__l114"
                  sx={{ textOverflow: 'ellipsis', textAlign: 'center', wordBreak: 'break-word' }}
                  variant="h6"
                >
                  {quantityName}
                </Typography>
              </Box>
            )}
            <Typography
              id="account-impacts-item__components-account-impacts__typography__l122"
              sx={{ textOverflow: 'ellipsis', textAlign: 'center', wordBreak: 'break-word' }}
              variant="body2"
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      {showBcInfo && item?.scans && item.scans.length > 0 && (
        <BlockchainVerifiedBanner
          size={36}
          stroke={bannerStroke}
          scans={item.scans}
          walletAddress={walletAddress || ''}
          showWalletAddress={showWalletAddress}
          accountImage={accountImage}
          impactImage={image}
          impactName={subtitle}
        />
      )}

      {item?.ai_generated && <AIGeneratedBanner />}

      <CustomModalAnimated
        ariaLable={`modal-account-impact-details-${item.id}`}
        open={open}
        handleClose={handleClose}
      >
        <Box
          id="account-impacts-item__components-account-impacts__box__l139"
          sx={{
            zIndex: 101,
            width: '100vw',
            height: '100dvh',
            maxHeight: '100dvh',
            backgroundColor: theme.palette.common.white,

            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconButton
            id="account-impacts-item__components-account-impacts__iconbutton__l152"
            onClick={handleClose}
            sx={{
              color: theme.palette.common.black,
              alignSelf: 'flex-end',
              m: isMobile ? '0px' : '5px',
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            id="account-impacts-item__components-account-impacts__box__l162"
            sx={{
              width: '100%',
              height: '100%',
              maxHeight: '100dvh',
              overflowY: 'auto',
            }}
          >
            <AccountImpactDetails
              title={item.quantity}
              quantityName={item.quantity_type_name || item.custom_quantity_type}
              subtitle={item.name}
              image={item.image_medium || item.image}
              unSdg={item.un_sdg || []}
              media={item.media || []}
              mainMedia={item.mainMedia || []}
              description={item.description}
              likes={item.totalLikes}
              liked={item.liked}
              impactId={item.id}
              showUnSdg={true}
              aiGenerated={item?.ai_generated}
            />
          </Box>
        </Box>
      </CustomModalAnimated>
    </Box>
  )
}
